import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Menu, Typography, Space, Tooltip, Avatar } from "antd";
import { useWindowSize } from "../../hooks/useWindowDimensions";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  TeamOutlined,
  SettingOutlined,
  HistoryOutlined,
  SafetyOutlined,
  BankOutlined,
  BuildOutlined,
} from "@ant-design/icons";
import getAccess from "../../utilities/getAccess";
import filterTable from "../../utilities/filterTable";
import Logo from "../../components/Logo/index";
import "./_sidebarNew.scss";

const { Sider } = Layout;
const { Text } = Typography;

const SidebarNew = ({ show, setSHow }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.fetchMe);
  const fetchMeStatus = useSelector((state) => state.user.fetchMeStatus);
  const windowSize = useWindowSize();
  const isMobile = windowSize <= 768;

  // Icon mapping - using Ant Design icons instead of images
  const iconMap = {
    Home: DashboardOutlined,
    userImg: UserOutlined,
    Groupe: FileTextOutlined,
    fire: FileTextOutlined,
    clock: ClockCircleOutlined,
    building: BankOutlined,
    Settings: SettingOutlined,
    History: HistoryOutlined,
    AdminPanelSettingsIcon: SafetyOutlined,
  };

  // Helper to get icon component
  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || DashboardOutlined;
    return <IconComponent style={{ fontSize: 18 }} />;
  };

  // Build menu items with permissions
  const menuItems = useMemo(() => {
    const paths = [
      {
        key: "dashboard",
        icon: <DashboardOutlined style={{ fontSize: 18 }} />,
        label: "Dashboard",
        path: "/",
      },
      getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "USER",
        "EDIT"
      ) && {
        key: "employees",
        icon: <TeamOutlined style={{ fontSize: 18 }} />,
        label: "Employees",
        path: "/employees",
      },
      // My Space hidden for Team Lead role
      getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "MY LEAVES",
        "LIST"
      ) &&
        user?.role?.code !== "TEAM LEAD" && {
          key: "myspace",
          icon: <FileTextOutlined style={{ fontSize: 18 }} />,
          label: "My Space",
          path: "/myspace",
        },
      getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "LEAVE",
        "LIST"
      ) && {
        key: "leaves",
        icon: <FileTextOutlined style={{ fontSize: 18 }} />,
        label: "Leaves",
        path: "/leaves",
      },
      getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "AUTHORISATION",
        "LIST"
      ) && {
        key: "authorisations",
        icon: <ClockCircleOutlined style={{ fontSize: 18 }} />,
        label: "Authorisation",
        path: "/authorisations",
      },
      getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "REMOTE REQUEST",
        "LIST"
      ) && {
        key: "remote-requests",
        icon: <HomeOutlined style={{ fontSize: 18 }} />,
        label: "Remote Requests",
        path: "/remote-requests",
      },
      getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "HEADQUARTER",
        "LIST"
      ) && {
        key: "headquarters",
        icon: <BankOutlined style={{ fontSize: 18 }} />,
        label: "Headquarters",
        path: "/headquarters",
      },
      (getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "DEPARTMENT",
        "LIST"
      ) ||
        getAccess(
          user?.permissions,
          user?.permissionGroups?.[0]?.permissions,
          "HOLIDAY",
          "LIST"
        ) ||
        getAccess(
          user?.permissions,
          user?.permissionGroups?.[0]?.permissions,
          "LEAVE TYPE",
          "LIST"
        ) ||
        getAccess(
          user?.permissions,
          user?.permissionGroups?.[0]?.permissions,
          "CURRENT USER",
          "EDIT"
        ) ||
        getAccess(
          user?.permissions,
          user?.permissionGroups?.[0]?.permissions,
          "PERMISSION",
          "LIST"
        )) && {
        key: "settings",
        icon: <SettingOutlined style={{ fontSize: 18 }} />,
        label: "Settings",
        path: "/settings/user-settings",
        children: [
          getAccess(
            user?.permissions,
            user?.permissionGroups?.[0]?.permissions,
            "CURRENT USER",
            "EDIT"
          ) && {
            key: "user-settings",
            label: "User Settings",
            path: "/settings/user-settings",
          },
          getAccess(
            user?.permissions,
            user?.permissionGroups?.[0]?.permissions,
            "DEPARTMENT",
            "LIST"
          ) && {
            key: "departements",
            label: "Departement",
            path: "/settings/list-departement",
          },
          getAccess(
            user?.permissions,
            user?.permissionGroups?.[0]?.permissions,
            "HOLIDAY",
            "LIST"
          ) && {
            key: "holidays",
            label: "Holidays",
            path: "/settings/holidays",
          },
          getAccess(
            user?.permissions,
            user?.permissionGroups?.[0]?.permissions,
            "LEAVE TYPE",
            "CREATE"
          ) && {
            key: "leave-settings",
            label: "Leave Settings",
            path: "/settings/leave-settings",
          },
        ].filter(Boolean),
      },
      getAccess(
        user?.permissions,
        user?.permissionGroups?.[0]?.permissions,
        "PERMISSION",
        "LIST"
      ) && {
        key: "permissions",
        icon: <SafetyOutlined style={{ fontSize: 18 }} />,
        label: "Permissions",
        path: "/permissions/admin-users",
        children: [
          {
            key: "admin-users",
            label: "Admin Users",
            path: "/permissions/admin-users",
          },
          getAccess(
            user?.permissions,
            user?.permissionGroups?.[0]?.permissions,
            "PERMISSION GROUP",
            "EDIT"
          ) && {
            key: "group-permissions",
            label: "Group Permissions",
            path: "/permissions/group-permissions",
          },
        ].filter(Boolean),
      },
      // {
      //   key: "history",
      //   icon: <HistoryOutlined style={{ fontSize: 18 }} />,
      //   label: "History",
      //   path: "/history",
      // },
    ];

    return filterTable(paths);
  }, [user]);

  // Get selected keys based on current path
  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    
    // Check for exact matches first
    for (const item of menuItems) {
      if (item.path === path) {
        return [item.key];
      }
      // Check submenu items
      if (item.children) {
        for (const child of item.children) {
          if (child.path === path) {
            return [child.key];
          }
        }
      }
    }

    // Check for partial matches (for nested routes)
    for (const item of menuItems) {
      if (path.startsWith(item.path) && item.path !== "/") {
        return [item.key];
      }
      if (item.children) {
        for (const child of item.children) {
          if (path.startsWith(child.path)) {
            return [child.key];
          }
        }
      }
    }

    return [];
  }, [location.pathname, menuItems]);

  // Get open keys (expanded submenus)
  const openKeys = useMemo(() => {
    const path = location.pathname;
    const keys = [];

    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (path.startsWith(child.path)) {
            keys.push(item.key);
            break;
          }
        }
      }
    }

    return keys;
  }, [location.pathname, menuItems]);

  const [openKeysState, setOpenKeysState] = useState(openKeys);

  // Handle menu click
  const handleMenuClick = ({ key }) => {
    // Find the clicked item
    const findItem = (items, targetKey) => {
      for (const item of items) {
        if (item.key === targetKey) {
          return item;
        }
        if (item.children) {
          const found = findItem(item.children, targetKey);
          if (found) return found;
        }
      }
      return null;
    };

    const clickedItem = findItem(menuItems, key);
    if (clickedItem && clickedItem.path) {
      navigate(clickedItem.path);
      // Collapse sidebar on mobile after navigation
      if (isMobile) {
        setTimeout(() => setSHow(false), 100);
      }
    }
  };

  // Transform menu items for Ant Design Menu component
  const transformMenuItems = (items) => {
    return items
      .filter(Boolean)
      .map((item) => {
        const menuItem = {
          key: item.key,
          icon: item.icon,
          label: item.label,
        };

        if (item.children && item.children.length > 0) {
          menuItem.children = transformMenuItems(item.children);
        }

        return menuItem;
      });
  };

  const antdMenuItems = useMemo(() => {
    return transformMenuItems(menuItems);
  }, [menuItems]);

  if (fetchMeStatus !== "succeeded") {
    return null;
  }


  return (
    <>
      {/* Overlay for mobile */}
      {isMobile && show && (
        <div
          className="sidebar-overlay"
          onClick={() => setSHow(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            zIndex: 999,
            transition: "opacity 0.3s ease",
          }}
        />
      )}
      <Sider
        trigger={null}
        collapsible
        collapsed={!show}
        width={230}
        collapsedWidth={isMobile ? 0 : 80}
        className={`modern-sidebar ${isMobile && !show ? 'sidebar-hidden-mobile' : ''}`}
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: isMobile && !show ? '-230px' : 0,
          top: 0,
          bottom: 0,
          boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
          zIndex: 1000,
          transition: "all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)",
        }}
        theme="light"
      >
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {show ? (
            <div className="logo-container">
              <Logo />
            </div>
          ) : (
            <div className="logo-collapsed">
              <Logo />
            </div>
          )}
        </div>
      </div>

      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={openKeysState}
        onOpenChange={setOpenKeysState}
        onClick={handleMenuClick}
        items={antdMenuItems}
        className="modern-sidebar-menu"
        style={{
          borderRight: 0,
          backgroundColor: "transparent",
          padding: "16px 0",
        }}
      />

      {/* User info and collapse toggle at bottom */}
      <div className="sidebar-footer">
        {show && user ? (
          <div className="sidebar-user-info-with-toggle">
            <Space size="small" style={{ width: "100%", padding: "0 16px", flex: 1 }}>
              <Avatar
                size="small"
                icon={<UserOutlined />}
                src={user?.img ? `${process.env.REACT_APP_IMG_URL}/${user.img}` : null}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ fontSize: 12, display: "block" }}>
                  {user?.fullName || "User"}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {user?.jobTitle || "Employee"}
                </Text>
              </div>
            </Space>
            <div className="sidebar-toggle-next-to-user" onClick={() => setSHow(!show)}>
              <MenuFoldOutlined style={{ fontSize: 16, cursor: "pointer", color: "#667eea" }} />
            </div>
          </div>
        ) : (
          <div className="sidebar-toggle-bottom" onClick={() => setSHow(!show)}>
            <MenuUnfoldOutlined style={{ fontSize: 18, cursor: "pointer", color: "#667eea" }} />
          </div>
        )}
      </div>
    </Sider>
    </>
  );
};

export default SidebarNew;

